import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Play, Pause } from 'lucide-react';

const SnakeGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  const gameLoopRef = useRef(null);
  
  // Game state
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [food, setFood] = useState({ x: 15, y: 15 });
  const [direction, setDirection] = useState('right');
  const [gameSpeed, setGameSpeed] = useState(150);
  
  const gridSize = 20;
  const cellSize = 20;

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setSnake([{ x: 10, y: 10 }]);
    setFood({ x: 15, y: 15 });
    setDirection('right');
    setGameSpeed(150);
    setIsPaused(false);
  };

  const pauseGame = () => {
    setIsPaused(!isPaused);
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setSnake([{ x: 10, y: 10 }]);
    setFood({ x: 15, y: 15 });
    setDirection('right');
    setGameSpeed(150);
    setIsPaused(false);
  };

  const generateFood = useCallback(() => {
    let newFood;
    do {
      newFood = {
        x: Math.floor(Math.random() * gridSize),
        y: Math.floor(Math.random() * gridSize)
      };
    } while (snake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    setFood(newFood);
  }, [snake]);

  const moveSnake = useCallback(() => {
    if (!isPlaying || gameOver || isPaused) return;

    setSnake(prevSnake => {
      const newSnake = [...prevSnake];
      const head = { ...newSnake[0] };

      // Move head based on direction
      switch (direction) {
        case 'up':
          head.y = (head.y - 1 + gridSize) % gridSize;
          break;
        case 'down':
          head.y = (head.y + 1) % gridSize;
          break;
        case 'left':
          head.x = (head.x - 1 + gridSize) % gridSize;
          break;
        case 'right':
          head.x = (head.x + 1) % gridSize;
          break;
        default:
          break;
      }

      // Check collision with self
      if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        setGameOver(true);
        setIsPlaying(false);
        if (score > highScore) {
          setHighScore(score);
        }
        return prevSnake;
      }

      // Check collision with food
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => prev + 10);
        setGameSpeed(prev => Math.max(50, prev - 2));
        generateFood();
        newSnake.unshift(head);
      } else {
        newSnake.unshift(head);
        newSnake.pop();
      }

      return newSnake;
    });
  }, [isPlaying, gameOver, isPaused, direction, food, score, highScore, generateFood]);

  useEffect(() => {
    if (isPlaying && !gameOver && !isPaused) {
      gameLoopRef.current = setInterval(moveSnake, gameSpeed);
    }
    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [isPlaying, gameOver, isPaused, moveSnake, gameSpeed]);

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver && !isPaused) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver, isPaused]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isPlaying || gameOver) return;

      switch (e.code) {
        case 'ArrowUp':
          e.preventDefault();
          if (direction !== 'down') setDirection('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (direction !== 'up') setDirection('down');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (direction !== 'right') setDirection('left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (direction !== 'left') setDirection('right');
          break;
        case 'Space':
          e.preventDefault();
          if (!isPlaying) {
            startGame();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying, gameOver, direction]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Cyber Snake
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Use arrow keys to control the snake. Eat the food to grow and survive as long as possible!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-red/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-red mb-4">
            Game Over!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Score: {score} | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-red text-neon-red hover:bg-neon-red hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Game Canvas */}
      <div className="cyber-card p-6">
        <div className="relative">
          {/* Pause Button */}
          {isPlaying && !gameOver && (
            <button
              onClick={pauseGame}
              className="absolute top-4 right-4 z-10 cyber-button text-sm px-3 py-2"
            >
              {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
          )}
          
          {/* Game Grid */}
          <div 
            className="relative w-full h-80 bg-dark-100 border-2 border-neon-blue/30 rounded-lg overflow-hidden"
            style={{ background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 100%)' }}
          >
            {/* Grid Lines */}
            <div className="absolute inset-0 opacity-10">
              {Array.from({ length: gridSize + 1 }).map((_, i) => (
                <React.Fragment key={i}>
                  <div 
                    className="absolute bg-neon-blue/30"
                    style={{
                      left: `${(i * cellSize)}px`,
                      top: 0,
                      width: '1px',
                      height: '100%'
                    }}
                  />
                  <div 
                    className="absolute bg-neon-blue/30"
                    style={{
                      top: `${(i * cellSize)}px`,
                      left: 0,
                      height: '1px',
                      width: '100%'
                    }}
                  />
                </React.Fragment>
              ))}
            </div>
            
            {/* Snake */}
            {snake.map((segment, index) => (
              <div
                key={index}
                className={`absolute bg-neon-green rounded-sm ${
                  index === 0 ? 'border-2 border-neon-blue' : ''
                }`}
                style={{
                  left: `${segment.x * cellSize}px`,
                  top: `${segment.y * cellSize}px`,
                  width: `${cellSize}px`,
                  height: `${cellSize}px`
                }}
              />
            ))}
            
            {/* Food */}
            <div
              className="absolute bg-neon-pink rounded-full animate-pulse"
              style={{
                left: `${food.x * cellSize}px`,
                top: `${food.y * cellSize}px`,
                width: `${cellSize}px`,
                height: `${cellSize}px`
              }}
            />
            
            {/* Score Display */}
            <div className="absolute top-4 left-4 text-neon-blue font-cyber font-bold text-2xl">
              {score}
            </div>
          </div>
        </div>
      </div>

      {/* Controls Info */}
      <div className="cyber-card p-6 mt-8">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            Controls
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue">↑</p>
              <p>Move Up</p>
            </div>
            <div>
              <p className="font-bold text-neon-green">↓</p>
              <p>Move Down</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink">←</p>
              <p>Move Left</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow">→</p>
              <p>Move Right</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnakeGame;
